import * as React from 'react';
import { useWindowSize } from 'react-use';
import { Box, useTheme } from '@mui/material';

import { EnhancedTable, ZoneCardList } from 'components';
import { convertZonesToTableData } from 'utils';
import { useAppDispatch, useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { ZoneActivateDialog, ZoneDeleteDialog, ZoneUpdateDialog } from 'dialogs/zone';
import { useInactiveZonesTableRows } from './zones.hook';
import { updateLayout } from 'store/slices/layout.slice';
import { IZone } from 'models';
import { useOutletContext } from 'react-router-dom';

type State = {
  zoneId: number | null;
  visibleZoneActivateDialog: boolean;
  visibleZoneUpdateDialog: boolean;
  visibleZoneDeleteDialog: boolean;
};

type ZonesContextType = {
  zones: IZone[];
  loading: boolean;
};

export const ZonesInactivePage = () => {
  const dispatch = useAppDispatch();
  const { isAdmin } = useAppSelector(selectCurrentUserInfo);
  const theme = useTheme();
  const { width } = useWindowSize();
  const [{ zoneId, visibleZoneActivateDialog, visibleZoneUpdateDialog, visibleZoneDeleteDialog }, setState] =
    React.useState<State>({
      zoneId: null,
      visibleZoneActivateDialog: false,
      visibleZoneUpdateDialog: false,
      visibleZoneDeleteDialog: false,
    });

  const { zones, loading } = useOutletContext<ZonesContextType>();

  const [, columnKeys, columns, rows] = useInactiveZonesTableRows({
    setState,
    loading,
    zones,
  });

  React.useEffect(() => {
    dispatch(updateLayout({ headerTitle: 'Zones' }));
  }, []);

  return (
    <>
      {width < theme.breakpoints.values.sm ? (
        <Box sx={{ paddingTop: 2 }}>
          <ZoneCardList
            zones={rows.sort((a, b) => (a.floorInfo > b.floorInfo ? 1 : a.floorInfo < b.floorInfo ? -1 : 0))}
            noDataLabel="No inactive zone"
          />
        </Box>
      ) : (
        <EnhancedTable
          config={{ toolbar: false, rowCheckable: false }}
          loading={loading}
          loadingLabel={'Loading inactive zones'}
          noDataLabel={'No inactive zones'}
          data={rows}
          convertFunctionForDataToTableData={convertZonesToTableData(isAdmin)}
          headColumns={columns}
          columnKeys={columnKeys}
          defaultSortColumnName="floorInfo"
          defaultSortDirection="asc"
          styles={{ tableStyle: { minWidth: 0 } }}
          initialRowsPerPage={10}
          visiblePagination
          rowActions={{}}
        />
      )}

      {typeof zoneId === 'number' && (
        <>
          <ZoneActivateDialog
            visible={visibleZoneActivateDialog}
            setVisible={(visibleZoneActivateDialog: boolean) =>
              setState((prev) => ({ ...prev, visibleZoneActivateDialog }))
            }
            zoneId={zoneId}
          />

          <ZoneUpdateDialog
            visible={visibleZoneUpdateDialog}
            setVisible={(visibleZoneUpdateDialog: boolean) =>
              setState((prev) => ({ ...prev, visibleZoneUpdateDialog }))
            }
            zoneId={zoneId}
          />

          <ZoneDeleteDialog
            visible={visibleZoneDeleteDialog}
            setVisible={(visibleZoneDeleteDialog: boolean) =>
              setState((prev) => ({ ...prev, visibleZoneDeleteDialog }))
            }
            zoneId={zoneId}
          />
        </>
      )}
    </>
  );
};
